import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import ProjectFacilitationSection from "../../projects/components/ProjectFacilitationSection";
import { useFacilitatorCapabilities } from "../context/FacilitatorCapabilityContext";

import {
  ApplicationButton,
  ApplicationContextualHelp,
  ApplicationPage,
  ApplicationSectionHeader,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../components/application";

export default function FacilitatePage() {
  const navigate = useNavigate();
  const capabilities = useFacilitatorCapabilities();
  const { refresh } = capabilities;
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ApplicationPage
      className="av-facilitate-page"
      variant="workspace"
    >
      <ApplicationViewHeader
        contextLabel="Facilitated participation"
        title="Facilitate"
        description="Run activities, manage assignments, and oversee projects."
      />

      <section aria-labelledby="facilitate-live-title">
        <ApplicationSectionHeader
          title="Live sessions"
          titleId="facilitate-live-title"
        />
        <ApplicationSurface className="av-facilitate-page__primary-card" variant="action">
          <ApplicationSectionHeader
            title={<span className="av-app-section-header__title-row"><span>Start a live session</span><ApplicationContextualHelp label="About live sessions" title="About live sessions"><p>Start an activity for participants to complete together in real time. You select the activity and share the session code; participants join from <Link to="/app/participate">Participate</Link> → Join a live session.</p></ApplicationContextualHelp></span>}
          />
          <p>Choose an activity, then open its live moderation flow.</p>
          <ApplicationButton onClick={() => navigate("/app/facilitate/live")}>Start a live session</ApplicationButton>
        </ApplicationSurface>
      </section>

      <section aria-labelledby="facilitate-assignments-title">
        <ApplicationSectionHeader
          title="Assignments"
          titleId="facilitate-assignments-title"
          description="Assignments use activities. Create or reuse an activity, then choose participants, dates and instructions."
        />
        <div className="av-facilitate-page__primary-actions">
        {["idle", "loading"].includes(capabilities.status) ? (
          <ApplicationStateRegion status="loading" message="Checking activity and Assignment access…" />
        ) : <>
        {capabilities.canAuthorActivities ? (
          <ApplicationSurface
            className="av-facilitate-page__primary-card"
            variant="action"
          >
            <ApplicationSectionHeader title="Activities" />
            <p>
              Create and manage reusable activities.
            </p>
            <ApplicationButton
              onClick={() => navigate("/app/facilitate/activities")}
            >
              Manage activities
            </ApplicationButton>
          </ApplicationSurface>
        ) : capabilities.status === "unavailable" || capabilities.activityAuthorDecision?.effect === "indeterminate" ? (
          <ApplicationSurface className="av-facilitate-page__primary-card">
            <ApplicationSectionHeader title="Activities" />
            <p>Activity authoring access could not be checked.</p>
            <ApplicationButton onClick={capabilities.refresh}>Retry activity access check</ApplicationButton>
          </ApplicationSurface>
        ) : null}

        {capabilities.canManageAssignments ? (
          <ApplicationSurface
            className="av-facilitate-page__primary-card"
            variant="action"
          >
            <ApplicationSectionHeader
              title="Assignments"
            />
            <p>
              Choose an activity, participants, dates, and assignment guidance.
            </p>
            <ApplicationButton
              onClick={() => navigate("/app/assignments")}
            >
              Manage assignments
            </ApplicationButton>
          </ApplicationSurface>
        ) : capabilities.status === "unavailable" || capabilities.assignmentManageDecision?.effect === "indeterminate" ? (
          <ApplicationSurface className="av-facilitate-page__primary-card">
            <ApplicationSectionHeader title="Assignments" />
            <p>Assignment access could not be checked.</p>
            <ApplicationButton onClick={capabilities.refresh}>Retry access check</ApplicationButton>
          </ApplicationSurface>
        ) : (
          <ApplicationSurface
            className="av-facilitate-page__primary-card"
          >
            <ApplicationSectionHeader
              title={(
                <span className="av-app-section-header__title-row">
                  <span>Assignments</span>
                  <ApplicationContextualHelp
                    label="About assignments"
                    title="About assignments"
                  >
                    <p>
                      Assignments will let you make specific All-Voiced
                      activities available to selected participants for them to
                      complete on their own time. You’ll create and manage
                      assignments here in Facilitate, and participants will
                      access their assigned activities from{" "}
                      <Link to="/app/participate">Participate</Link>.
                    </p>
                  </ApplicationContextualHelp>
                </span>
              )}
            />
            <p className="catalog-resource-guide__status-label">
              <strong>Coming soon</strong>
            </p>
            <p>
              Create and manage activities for
              specific participants.
            </p>
          </ApplicationSurface>
        )}
        </>}
        </div>
      </section>

      <ProjectFacilitationSection />
    </ApplicationPage>
  );
}
